<template>
  <div data-view="labels">
    <router-view v-slot="{ Component }">
      <component :is="Component" @saved="onSaved" />
    </router-view>

    <dialog-confirm
      :text="
        $t('are_you_sure_you_want_to_delete_this_label', {
          labelName: labelForDeletion.name,
        })
      "
      :is-on="isRemoveLabelModalOn"
      :is-loading="isRemovingLabel"
      :on-cancel="toggleDeleteLabelModal"
      :on-confirm="deleteLabel"
    />

    <layout-logged-frame :title="$t('labels') + ` (${totalCount})`">
      <template v-slot:header-actions>
        <v-btn
          v-if="canStoreLabel"
          color="primary"
          @click="$router.push({ name: 'labels-new', query: $route.query })"
        >
          <v-icon>add</v-icon>
          {{ $t('add_new_label') }}
        </v-btn>
      </template>

      <template v-slot:content>
        <LabelsFilters @filter="onFilter" @clearFilters="onClearFilters" />

        <table-base
          :columns="columns"
          :items="labels"
          :pages="totalPages"
          :pagination="pagination"
          :is-loading="isLoading"
          has-custom-items-template
          :on-select-item="selectLabel"
          :on-update-pagination="onUpdatePagination"
          :on-change-page="onChangePage"
          :on-action="onAction"
          :sync-to-url="true"
          @init-pagination="onInitPagination"
        >
          <template v-slot:item-cell="scope">
            <div
              v-if="scope.cell.column.value === 'actions' && canDeleteLabel"
              class="remove-label-box"
              @click="
                (event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onAction(scope.cell.item, scope.cell.column.action);
                }
              "
            >
              <v-icon>{{ scope.cell.column.icon }}</v-icon>
              {{ $t('delete') }}
            </div>

            <template v-if="scope.cell.column.value === 'color'">
              <labels-color
                :color="scope.cell.item.color.name.toLowerCase()"
                :label="scope.cell.item.color.name"
              />
            </template>

            <template v-if="scope.cell.column.value === 'type'">
              {{ displayColumnType(scope.cell.item) }}
            </template>

            <template
              v-else-if="
                scope.cell.column.value !== 'actions' &&
                  scope.cell.column.value !== 'color'
              "
            >
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </template>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import LabelsFilters from '../../components/forms/filters/Label.vue';
  import {extractNestedProp} from '@/utils';
  import columns from '../../config/labels/columns';
  import viewWithFilters from '../../mixins/viewWithFilters';
  import type { LabelListItem } from '@/types';
  import type { LabelsQueryParams } from '@/api/merchy/labels';
  import type { TableColumn } from '@/components/table/types';

  export default defineComponent({
    mixins: [viewWithFilters],
    components: {
      LabelsFilters,
    },
    data() {
      return {
        columns: columns as TableColumn[],
        pagination: {
          order: 'desc' ,
          page: 1,
          rowsPerPage: 15,
          sortBy: 'updated_at',
          totalItems: 0,
          search: '',
        },
        labelForDeletion: {} as Partial<LabelListItem>,
        query: { per_page: 15 } as LabelsQueryParams,
        isRemoveLabelModalOn: false,
        isRemovingLabel: false,
      };
    },
    computed: {
      labels() {
        return this.$store.getters['labels/items'];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },

      canDeleteLabel() {
        return this.$store.getters['auth/isAuthorized']([{ subject: 'label', actions: ['delete'] }]);
      },
      canStoreLabel() {
        return this.$store.getters['auth/isAuthorized']([{ subject: 'label', actions: ['store'] }]);
      },
    },
    created() {
      if (!this.$store.getters['auth/isAuthorized']([{ subject: 'label', actions: ['delete'] }])) {
        this.columns = this.columns.filter(
          (column) => column.value !== 'actions',
        );
      }
    },
    methods: {
      // Emitted by the add/edit dialog routes after a successful save. Before the
      // dialogs became child routes the save handler lived here and refetched
      // directly — this keeps the refetch in the same place, with this view's own
      // query, and out of the URL.
      onSaved() {
        this.getItems(this.query);
      },
      getItems(query: LabelsQueryParams = {}) {
        this.isLoading = true;
        this.pagination.order = query.order;

        return this.$store
          .dispatch('labels/getItems', query)
          .then((res) => {
            const { itemsMeta } = this.$store.getters['labels/itemsData'];
            const { total, current_page, last_page } = itemsMeta;

            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.isLoading = false;

            return res;
          })
          .catch((err) => {
            this.isLoading = false;
            return err;
          });
      },
      selectLabel(label: LabelListItem) {
        this.$store.dispatch('labels/setActiveItem', label);
        this.$router.push({ name: 'labels-edit', params: { id: label.id }, query: this.$route.query });
      },
      toggleDeleteLabelModal() {
        this.isRemoveLabelModalOn = !this.isRemoveLabelModalOn;
      },
      onAction(label: LabelListItem, action: string) {
        if (action === 'delete') {
          this.labelForDeletion = label;
          this.isRemoveLabelModalOn = true;
        }
      },
      deleteLabel() {
        this.isRemovingLabel = true;

        return this.$store
          .dispatch('labels/remove', this.labelForDeletion)
          .then((res) => {
            this.labelForDeletion = {};
            this.isRemovingLabel = false;
            this.toggleDeleteLabelModal();
            this.getItems(this.query);
            return res;
          })
          .catch((err) => {
            this.isRemovingLabel = false;
            return err;
          });
      },
      getColumnValue(value: string) {
        return value.indexOf('.') === -1 ? value : value.split('.').join('-');
      },
      displayColumnValue(item: LabelListItem, columnValue: string) {
        return extractNestedProp(item, columnValue);
      },
      displayColumnType(item: LabelListItem) {
        return item.is_system_label ? 'System' : 'General';
      }
    },
  });
</script>

<style lang="scss">
@use 'sass:map';
@use '../../assets/styles/theme' as *;

[data-view='labels'] {
  .remove-label-box {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    color: rgb(var(--v-theme-error));

    .v-icon {
      color: rgb(var(--v-theme-error));
      margin: 0 4px 0 0;
    }
  }

  .v-datatable {
    min-width: $table-min-width;
  }
}
</style>
